import type React from 'react'

import { useCompany } from '@/hooks'
import type { AssetState } from '@/types/asset-types'
import { formatSpeed, formatTemperature } from '@/utils/conversions'

import type { DetailItem } from './DetailCard'
import {
  bearingDirection,
  mapAirControl,
  mapEngineSpeed,
  mapPowerSource,
  formatDate,
  formatNumber,
  convertTemp,
  getVisibleSections,
  REEFER_INTERFACE,
} from './utils'

type Units = 'Imperial' | 'Metric'

/** The label/value rows for every card in the asset detail view. */
export default function useAssetDetailItems(asset: AssetState, companyId: number | undefined, units: Units) {
  const { companies } = useCompany()
  const company = companies.find((c) => c.id === companyId)
  const speedUnit = units === 'Metric' ? 'kph' : 'mph'
  const tempUnit = units === 'Metric' ? 'C' : 'F'
  const powerOn = asset.powerOn

  // Only Thermo King and Carrier Advanced units report the additional reefer data, and
  // only companies with it switched on see it.
  const reeferMake = asset.reefer.make
  const showAdditionalReeferData =
    !!company?.enableReeferFridgeData && (reeferMake === 'Thermo King' || reeferMake === 'Carrier: Advanced')
  const showCurrentReeferData = asset.fridgeOnline && asset.fridgeAdditionalOnline
  // Refresh doesn't load reefer data for assets without a reefer interface (2) or without a
  // reefer (3), so the rows keep their labels but show nothing.
  const sections = getVisibleSections(asset)
  const hasNoReeferData =
    asset.reefer.interfaceTypeId === REEFER_INTERFACE.NONE ||
    asset.reefer.interfaceTypeId === REEFER_INTERFACE.NO_REEFER

  const na = (value: React.ReactNode): Pick<DetailItem, 'value' | 'valueClassName'> =>
    value === 'N/A' || value === '' ? { value: 'N/A', valueClassName: 'text-base-content/40' } : { value }

  const whenOn = (value: React.ReactNode): Pick<DetailItem, 'value' | 'valueClassName'> =>
    powerOn ? na(value) : { value: '' }

  const locationDetails: DetailItem[] = [
    { label: 'Address', ...na(asset.location.address ?? 'N/A') },
    ...(asset.location.geofence ? [{ label: 'Geofence', ...na(asset.location.geofence.name ?? 'Unknown') }] : []),
    { label: 'Average Speed', ...na(formatSpeed(asset.averageSpeed, speedUnit)) },
    { label: 'Maximum Speed', ...na(formatSpeed(asset.maximumSpeed, speedUnit)) },
    { label: 'Bearing', ...na(asset.bearing ? bearingDirection(asset.bearing) : 'N/A') },
  ]

  const reeferValue = (value: React.ReactNode): Pick<DetailItem, 'value' | 'valueClassName'> =>
    hasNoReeferData ? { value: '' } : na(showCurrentReeferData && value != null ? value : 'N/A')
  const reeferTemp = (celsius: number | null | undefined) => reeferValue(formatTemperature(celsius, tempUnit))
  // The per-zone rows only show for zones the asset has.
  const zoneRows = (label: string, temps: (number | null | undefined)[]): DetailItem[] =>
    temps
      .slice(0, asset.reefer.zoneCount)
      .map((celsius, idx) => ({ label: idx === 0 ? label : `${label} ${idx + 1}`, ...reeferTemp(celsius) }))

  // Mirrors refresh's fitasset page: four rows every make reports, then the make's own.
  const additional = asset.additional
  const additionalDetails: DetailItem[] = showAdditionalReeferData
    ? [
        { label: 'Battery Current (A)', ...reeferValue(additional.batteryCurrent) },
        { label: 'Engine Temp', ...reeferTemp(additional.coolantTemperature) },
        { label: 'Discharge Press (PSIG)', ...reeferValue(additional.compressorDischargePressure) },
        { label: 'Suction Press (PSIG)', ...reeferValue(additional.compressorSuctionPressure) },
        ...(reeferMake === 'Thermo King'
          ? [
              {
                label: 'ETV Position',
                // refresh's thermoKingETVPosition: the raw value is in eighths.
                ...reeferValue(
                  additional.evaporatorValvePosition != null ? Math.round(additional.evaporatorValvePosition * 8) : null
                ),
              },
              ...zoneRows('Evap Coil Temp', [
                additional.evaporatorOutletTemperatureZ1,
                additional.evaporatorOutletTemperatureZ2,
                additional.evaporatorOutletTemperatureZ3,
              ]),
            ]
          : [
              { label: 'SMV (%)', ...reeferValue(additional.compressorSMVPosition) },
              ...zoneRows('DTT', [
                additional.defrostTerminationTemperatureZ1,
                additional.defrostTerminationTemperatureZ2,
                additional.defrostTerminationTemperatureZ3,
              ]),
            ]),
      ]
    : []

  const allReeferDetails: DetailItem[] = [
    { label: 'Operational Mode', ...whenOn(asset.reefer?.operation ?? 'N/A') },
    { label: 'Air Control', ...whenOn(mapAirControl(asset.reefer?.airControl) ?? '') },
    { label: 'Intelliset', ...whenOn(asset.reefer?.intelliset ?? 'N/A') },
    { label: 'Power Source', ...whenOn(mapPowerSource(asset.reefer?.powerSource) ?? '') },
    { label: 'Engine Speed', ...whenOn(mapEngineSpeed(asset.reefer?.engineSpeed) ?? '') },
    { label: 'Make & Model', ...na(`${asset.reefer?.make ?? ''} ${asset.reefer?.model ?? ''}`.trim() || 'N/A') },
  ]

  const reeferDetails = sections.reeferMakeModelOnly
    ? allReeferDetails.filter((item) => item.label === 'Make & Model')
    : allReeferDetails

  const engineDetails: DetailItem[] = [
    { label: 'Electric Hours', ...na(asset.engine?.electricHours?.toLocaleString() ?? 'N/A') },
    { label: 'Diesel Hours', ...na(asset.engine?.dieselHours?.toLocaleString() ?? 'N/A') },
    { label: 'Reefer-On Hours', ...na(asset.engine?.reeferOnHours?.toLocaleString() ?? 'N/A') },
    { label: 'Compressor Hours', ...na(asset.engine?.compressorHours?.toLocaleString() ?? 'N/A') },
  ]

  const maintenanceDetails: DetailItem[] = [
    { label: 'Fuel', ...whenOn(formatNumber(asset.maintenance?.fuelPercent, 1, '%')) },
    { label: 'Battery', ...whenOn(formatNumber(asset.maintenance?.batteryVolts, 1, ' V')) },
    { label: 'Current Hours', ...na(asset.maintenance?.currentHours?.toLocaleString() ?? 'N/A') },
    { label: 'Due Hours', ...na(asset.maintenance?.dueHours?.toLocaleString() ?? 'N/A') },
    { label: 'Due Date', ...na(formatDate(asset.maintenance?.dueDate)) },
  ]

  const formatTempName = (temp: string) => (temp === 'Front Temp' ? 'Front' : temp === 'Rear Temp' ? 'Rear' : temp)
  const monitoringPointDetails: DetailItem[] =
    asset.monitoringPoints.length > 0
      ? asset.monitoringPoints.map((point) => ({
          label: formatTempName(point.name),
          ...na(convertTemp(point.temperature, units)),
        }))
      : [{ label: 'No monitoring points', value: '' }]

  const doorDetails: DetailItem[] =
    asset.doors.length > 0
      ? asset.doors.map((door) => ({
          label: door.name,
          value: door.open ? 'Open' : 'Closed',
        }))
      : [{ label: 'No doors', value: '' }]

  return {
    sections,
    locationDetails,
    additionalDetails,
    reeferDetails,
    engineDetails,
    maintenanceDetails,
    monitoringPointDetails,
    doorDetails,
  }
}
